import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  SparklesIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome, useHseBusyAction } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  createRiskAssessment,
  deleteRiskAssessment,
  excludeRiskAssessment,
  exportRiskAssessmentPdf,
  fetchRiskCampaign,
  fetchRiskCatalog,
  fetchRiskTiltak,
  linkRiskTiltak,
  suggestRiskCampaignDrafts,
  updateRiskAssessment,
} from "../../../../api/hse/risk";
import { HseRowActionsMenu } from "../../../../components/dashboard/hse/HseRowActionsMenu";
import { HseGuideButton, HseModuleGuide } from "../../../../components/dashboard/hse/HseModuleGuide";
import { AssessRiskDialog } from "../../../../components/dashboard/hse/risk/AssessRiskDialog";
import { RiskLevelBadge } from "../../../../components/dashboard/hse/risk/RiskLevelBadge";
import { RiskSubnav } from "../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseListCard, HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";
import {
  campaignProgress,
  emptyRiskForm,
  formatDisplayDate,
  isExcluded,
  RISK_PATHS,
  riskApiError,
} from "../../../../components/dashboard/hse/risk/riskUtils";

const inputClass =
  "mt-2 w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1463C7]";

export const HseKsRiskCampaign = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const canCreate = useUserHasAccess({ role: "hse_ks_risk", action: "create" });
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [addMode, setAddMode] = useState(null);
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [themeId, setThemeId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyRiskForm());
  const [linkedTiltak, setLinkedTiltak] = useState([]);
  const [saving, setSaving] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ industry: "", fag: "" });
  const [suggesting, setSuggesting] = useState(false);
  const { run, isBusy } = useHseBusyAction();

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const record = await fetchRiskCampaign(campaignId);
      setCampaign(record);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.campaign.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    fetchRiskCatalog()
      .then((catalog) => {
        setThemes(catalog.themes || []);
        setCategories(catalog.categories || []);
      })
      .catch(() => {
        setThemes([]);
        setCategories([]);
      });
  }, []);

  useEffect(() => {
    if (searchParams.get("add") === "1") setAddMode("choose");
    if (searchParams.get("suggest") === "1") setAddMode("suggest");
  }, [searchParams]);

  const progress = campaignProgress(campaign || {});
  const active = campaign?.risks || [];
  const excluded = campaign?.skipped || [];

  const openAssess = (next = {}) => {
    setForm(
      emptyRiskForm({
        ...next,
        hse_risk_campaign_id: campaignId,
      })
    );
    setLinkedTiltak(next.tiltak || []);
    setDialogOpen(true);
  };

  const persist = async (values, { excluded: skip, stay } = {}) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        hse_risk_campaign_id: campaignId,
        skip_assessment: Boolean(skip),
        status: skip ? "skipped" : values.risk_level ? "assessed" : values.status || "draft",
      };
      const saved = values.id
        ? skip
          ? await excludeRiskAssessment(values.id, values.skip_reason || values.excluded_reason)
          : await updateRiskAssessment(values.id, payload)
        : await createRiskAssessment(payload);
      toast.success(
        skip ? t("hse.risk.excluded_saved") : values.id ? t("hse.risk.updated") : t("hse.risk.created")
      );
      if (saved?.id) setForm((current) => ({ ...current, ...saved, id: saved.id }));
      if (!stay) {
        setDialogOpen(false);
        setAddMode(null);
        setSearchParams({}, { replace: true });
        await load();
      }
      return saved;
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.save_failed"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const ensureSaved = async (values) => {
    if (values?.id) return values;
    const saved = await persist({ ...form, ...values, status: "draft" }, { stay: true });
    if (saved?.id) {
      setForm((current) => ({ ...current, ...saved, id: saved.id }));
    }
    return saved;
  };

  const handleLinkTiltak = async (tiltakId, created) => {
    const riskId = form.id;
    if (created) {
      setLinkedTiltak((current) => [...current, created]);
      return;
    }
    if (!riskId) {
      toast.error(t("hse.risk.tiltak.save_first"));
      return;
    }
    try {
      await linkRiskTiltak(riskId, tiltakId);
      setLinkedTiltak(await fetchRiskTiltak(riskId));
      toast.success(t("hse.risk.tiltak.linked"));
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.tiltak.save_failed"));
    }
  };

  const handleSuggest = async (event) => {
    event.preventDefault();
    if (!suggestForm.industry.trim() && !suggestForm.fag.trim()) {
      toast.error(t("hse.risk.suggest.error_industry"));
      return;
    }
    setSuggesting(true);
    try {
      const response = await suggestRiskCampaignDrafts(campaignId, {
        industry: suggestForm.industry,
        fag: suggestForm.fag,
        count: 5,
      });
      toast.success(t("hse.risk.suggest.created", { count: (response.data || []).length }));
      setAddMode(null);
      setSearchParams({}, { replace: true });
      await load();
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.suggest.failed"));
    } finally {
      setSuggesting(false);
    }
  };

  const handleDeleteRisk = async (item) => {
    if (!window.confirm(t("hse.risk.delete_confirm", { title: item.title }))) return;
    try {
      await deleteRiskAssessment(item.id);
      toast.success(t("hse.risk.deleted"));
      load();
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.save_failed"));
    }
  };

  const riskActions = (item) => [
    {
      label: t("hse.risk.open"),
      icon: EyeIcon,
      action: () => navigate(`/dashboard/hse-ks/risk/${item.id}`),
    },
    {
      label: t("hse.risk.edit"),
      icon: PencilIcon,
      hidden: !canEdit,
      action: () => openAssess(item),
    },
    {
      label: t("hse.risk.tiltak.how"),
      icon: SparklesIcon,
      hidden: !canEdit,
      action: () => {
        openAssess(item);
      },
    },
    {
      label: t("hse.risk.pdf"),
      icon: PrinterIcon,
      disabled: isBusy(),
      loading: isBusy(`pdf-${item.id}`),
      action: () =>
        run(`pdf-${item.id}`, async () => {
          try {
            await exportRiskAssessmentPdf(item.id, `hse-risiko-${item.id}.pdf`);
            toast.success(t("hse.risk.pdf_ready"));
          } catch (err) {
            toast.error(err.message || t("hse.risk.pdf_failed"));
          }
        }),
    },
    {
      label: t("hse.risk.delete"),
      icon: TrashIcon,
      danger: true,
      hidden: !canEdit,
      action: () => handleDeleteRisk(item),
    },
  ];

  const renderRiskRow = (item) => (
    <tr key={item.id} className="hover:bg-slate-50">
      <td className="px-5 py-4">
        <button
          type="button"
          onClick={() => (canEdit ? openAssess(item) : navigate(`/dashboard/hse-ks/risk/${item.id}`))}
          className="text-left font-bold text-[#1463C7] hover:underline"
        >
          {item.title}
        </button>
      </td>
      <td className="px-5 py-4">
        <RiskLevelBadge level={item.risk_level} excluded={isExcluded(item)} />
      </td>
      <td className="px-5 py-4 text-slate-600">
        {item.category_name ||
          (item.category ? t(`hse.risk.category.${item.category}`, { defaultValue: item.category }) : "")}
      </td>
      <td className="px-5 py-4 text-slate-600">{formatDisplayDate(item.updated_at)}</td>
      <td className="px-5 py-4 text-right">
        <HseRowActionsMenu
          variant="labeled"
          label={t("hse.risk.more")}
          header={t("hse.risk.more_header")}
          items={riskActions(item)}
        />
      </td>
    </tr>
  );

  const selectedTheme = themes.find((theme) => theme.id === themeId);

  return (
    <HsePageChrome
      variant="list"
      title={campaign?.title || t("hse.risk.campaigns.title")}
      subtitle={
        campaign
          ? t("hse.risk.campaign.progress_label", { done: progress.done, total: progress.total })
          : t("hse.risk.campaigns.subtitle")
      }
      backTo={RISK_PATHS.hub}
      mainButton={
        canCreate
          ? {
              label: t("hse.risk.campaign.add_risk"),
              icon: PlusIcon,
              action: () => setAddMode("choose"),
            }
          : undefined
      }
    >
      <HsePageBody>
        <RiskSubnav active="campaigns" />
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Link to={RISK_PATHS.hub} className="text-sm font-semibold text-slate-600 hover:text-slate-900">
            ← {t("hse.risk.campaigns.title")}
          </Link>
          <HseGuideButton onClick={() => setGuideOpen(true)} />
        </div>

        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <ResourceError
            variant={!campaign ? "not_found" : "load_error"}
            errors={[error]}
            onRetry={load}
            backTo={RISK_PATHS.hub}
          />
        ) : (
          <div className="space-y-6">
            {addMode === "choose" ? (
              <div className="grid gap-3 sm:grid-cols-3">
                {["catalog", "custom", "suggest"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAddMode(mode)}
                    className="rounded-[18px] border border-slate-200 bg-white p-4 text-left hover:shadow-md"
                  >
                    <p className="text-sm font-extrabold text-slate-900">
                      {t(mode === "catalog" ? "hse.risk.add.common" : mode === "custom" ? "hse.risk.add.custom" : "hse.risk.add.suggest")}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {t(
                        mode === "catalog"
                          ? "hse.risk.add.common_hint"
                          : mode === "custom"
                            ? "hse.risk.add.custom_hint"
                            : "hse.risk.add.suggest_hint"
                      )}
                    </p>
                  </button>
                ))}
              </div>
            ) : null}

            {addMode === "catalog" ? (
              <div className="rounded-[18px] border border-slate-200 bg-white p-5">
                <p className="text-sm font-semibold text-slate-700">{t("hse.risk.add.pick_theme")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {themes.map((theme) => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setThemeId(theme.id)}
                      className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                        themeId === theme.id
                          ? "bg-[#1463C7] text-white"
                          : "border border-slate-200 text-slate-700"
                      }`}
                    >
                      {theme.title || t(`hse.risk.theme.${theme.id}`, { defaultValue: theme.id })}
                    </button>
                  ))}
                </div>
                {selectedTheme ? (
                  <ul className="mt-4 divide-y divide-slate-100">
                    {selectedTheme.challenges.map((challenge) => (
                      <li key={challenge.id}>
                        <button
                          type="button"
                          onClick={() =>
                            openAssess({
                              title:
                                challenge.title ||
                                t(`hse.risk.catalog.${challenge.id}`, { defaultValue: challenge.id }),
                              category: challenge.category || selectedTheme.category,
                              theme: selectedTheme.id,
                              challenge_key: challenge.id,
                              challenge_title: challenge.title,
                              challenge_kind: "common",
                              consequence: challenge.consequence || "",
                            })
                          }
                          className="w-full py-3 text-left text-sm font-bold text-slate-900 hover:bg-slate-50"
                        >
                          {challenge.title || t(`hse.risk.catalog.${challenge.id}`, { defaultValue: challenge.id })}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {addMode === "custom" ? (
              <div className="rounded-[18px] border border-slate-200 bg-white p-5">
                <p className="text-sm font-semibold text-slate-700">{t("hse.risk.add.pick_category")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() =>
                        openAssess({
                          challenge_kind: "custom",
                          hse_risk_category_id: category.id,
                          category: category.slug,
                          category_name: category.name,
                        })
                      }
                      className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {addMode === "suggest" ? (
              <form
                onSubmit={handleSuggest}
                className="space-y-4 rounded-[18px] border border-slate-200 bg-white p-5"
              >
                <p className="text-sm font-semibold text-slate-700">{t("hse.risk.suggest.campaign_hint")}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.suggest.industry")}</span>
                    <input
                      className={inputClass}
                      value={suggestForm.industry}
                      onChange={(event) =>
                        setSuggestForm((current) => ({ ...current, industry: event.target.value }))
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.suggest.fag")}</span>
                    <input
                      className={inputClass}
                      value={suggestForm.fag}
                      onChange={(event) =>
                        setSuggestForm((current) => ({ ...current, fag: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={suggesting}
                  className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {suggesting ? t("hse.risk.suggest.working") : t("hse.risk.suggest.run")}
                </button>
              </form>
            ) : null}

            <section>
              <h2 className="mb-3 text-sm font-extrabold text-slate-900">
                {t("hse.risk.campaign.risks_title")}
              </h2>
              <HseListCard className="mt-0">
                {active.length === 0 ? (
                  <div className="px-6 py-12">
                    <ResourceEmpty message={t("hse.risk.empty")} />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-5 py-3">{t("hse.risk.fields.title")}</th>
                          <th className="px-5 py-3">{t("hse.risk.fields.level")}</th>
                          <th className="px-5 py-3">{t("hse.risk.fields.category")}</th>
                          <th className="px-5 py-3">{t("hse.risk.campaigns.col.updated")}</th>
                          <th className="px-5 py-3 text-right">{t("hse.risk.actions")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">{active.map(renderRiskRow)}</tbody>
                    </table>
                  </div>
                )}
              </HseListCard>
            </section>

            {excluded.length > 0 ? (
              <section>
                <h2 className="mb-3 text-sm font-extrabold text-slate-900">{t("hse.risk.excluded.title")}</h2>
                <HseListCard className="mt-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <tbody className="divide-y divide-slate-100">{excluded.map(renderRiskRow)}</tbody>
                    </table>
                  </div>
                </HseListCard>
              </section>
            ) : null}
          </div>
        )}
      </HsePageBody>

      <AssessRiskDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialValues={form}
        saving={saving}
        linkedTiltak={linkedTiltak}
        categories={categories}
        onSave={(values) => persist(values)}
        onExclude={(values) => persist(values, { excluded: true })}
        onLinkTiltak={handleLinkTiltak}
        onEnsureSaved={ensureSaved}
      />
      <HseModuleGuide topic="risk" open={guideOpen} onClose={() => setGuideOpen(false)} />
    </HsePageChrome>
  );
};
